/**
 * ChatHintTab Component
 *
 * A slim tab flush against the right edge of the screen that reopens the
 * ChatDrawer once it has been closed (FLOW-840). Desktop only — below `md`
 * the drawer is full-width and the tab would sit on top of it; small screens
 * reach AI via the command palette, item-detail AI buttons, or the sidebar's
 * Chat nav.
 */
import { Component } from "solid-js";
import { modKey } from "../layout/shellShortcuts";

export interface ChatHintTabProps {
  onOpen: () => void;
  /**
   * True while the ChatDrawer is open. The tab stays mounted the whole time
   * (FLOW-840 I1) and is only pushed off-screen with a transform, mirroring
   * ChatDrawer.tsx's own translate-x-full/translate-x-0: ChatDrawer's focus
   * effect restores focus to whatever `previouslyFocused` element is still
   * `document.contains`-attached when it closes, and unmounting this button
   * (the old behaviour) detached it, stranding focus in the drawer.
   */
  hidden: () => boolean;
}

export const ChatHintTab: Component<ChatHintTabProps> = (props) => {
  return (
    <button
      type="button"
      aria-label="Open AI chat"
      title={`Open AI chat (${modKey()}J)`}
      data-chat-hint-tab
      // Out of the tab order while off-screen; `aria-hidden` alongside
      // `tabindex="-1"` is safe on a focusable element only once it's no
      // longer keyboard-reachable, which the tabindex already guarantees.
      tabIndex={props.hidden() ? -1 : undefined}
      aria-hidden={props.hidden() ? "true" : undefined}
      onClick={() => props.onOpen()}
      // py-3 px-1.5 + the w-3.5 icon below compute to 27x58px, 1px over
      // flow782-graphite-density.e2e.test.ts's 26px hit-target floor —
      // shrinking the icon below w-3.5 or adding visible text drops the
      // width or trips that spec's `padded` check. Don't add `hit-area`
      // here either: it would change the box and break the flush-right-edge
      // assertion in flow840-chat-hint-tab.e2e.test.ts.
      class={
        "hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-[var(--z-sticky)] flex-col items-center gap-1 py-3 px-1.5 bg-surface border border-r-0 border-line rounded-l text-ink-3 hover:text-ink hover:bg-sunken focus-ring transition-transform duration-200 ease-in-out " +
        (props.hidden()
          ? "translate-x-full pointer-events-none"
          : "translate-x-0")
      }
    >
      <span class="font-mono text-2xs text-signal-magenta" aria-hidden="true">
        ai
      </span>
      <svg
        class="w-3.5 h-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <path d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  );
};
